import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ContactJson } from 'app/types/entities'
import { formatDate } from 'app/date.utils'
import { useContactStore } from 'app/stores/StoreContext'
import { gqlFetchList } from 'app/lib/graphqlClient'

export type ConversionMetrics = {
  totalAttempts: number
  successRate: number
  failureRate: number
  byType: Record<string, { total: number; sent: number; failed: number }>
  recentFailures: unknown[]
  alertsTriggered: string[]
}

export type UseConversionMonitoringReturn = {
  metrics: ConversionMetrics
  activeTab: string
  handleTabChange: (tab: string) => void
  focusedContact: ContactJson | undefined
  openContactDrawer: boolean
  contactHandler: (contactId: string) => void
  closeDrawerHandler: () => void
  conversionTypeData: Array<{
    type: string
    total: number
    sent: number
    failed: number
    successRate: number
  }>
  recentAttemptsData: { hits: unknown[]; total: number }
  recentAttemptsColumns: any[]
  setFocusedContact: (c: ContactJson | undefined) => void
}

const defaultMetrics: ConversionMetrics = {
  totalAttempts: 0,
  successRate: 0,
  failureRate: 0,
  byType: {
    QUALIFIED_LEAD_GENERATED: { total: 0, sent: 0, failed: 0 },
    NEW_PATIENT_APPOINTMENT_SCHEDULED: { total: 0, sent: 0, failed: 0 },
    NEW_PATIENT_GENERATED: { total: 0, sent: 0, failed: 0 },
    ADDITIONAL_TREATMENT_REVENUE: { total: 0, sent: 0, failed: 0 },
  },
  recentFailures: [],
  alertsTriggered: [],
}

export function useConversionMonitoring(): UseConversionMonitoringReturn {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const contactStore = useContactStore()
  const [metrics, setMetrics] = useState<ConversionMetrics>(defaultMetrics)
  const [recentLogs, setRecentLogs] = useState<{ hits: unknown[]; total: number }>({ hits: [], total: 0 })
  const [activeTab, setActiveTab] = useState(searchParams.get(`tab`) || `Overview`)
  const [focusedContact, setFocusedContact] = useState<ContactJson | undefined>()
  const [openContactDrawer, setOpenContactDrawer] = useState<boolean>(
    () => searchParams.has(`drawer`) && searchParams.get(`drawer`) !== `new`,
  )

  const pageParam = searchParams.get(`page`)
  const fetchData = useCallback(() => {
    const page = searchParams.get(`page`) || `0`
    gqlFetchList<Record<string, unknown>>(
      'conversionLogs',
      { search: undefined, pagination: { page: Number(page), limit: 50 } },
      'id contactId locationId workspaceId orgId conversionType status gclid conversionDateTime conversionValue errorMessage googleAdsAccountId apiResponse createdDate updatedDate',
    )
      .then((data) => {
        // Compute metrics client-side from the logs
        const logs = data.hits
        const total = data.total
        const sent = logs.filter((l) => l.status === `sent`).length
        const failed = logs.filter((l) => l.status === `failed`).length
        const byType: Record<string, { total: number; sent: number; failed: number }> = {
          QUALIFIED_LEAD_GENERATED: { total: 0, sent: 0, failed: 0 },
          NEW_PATIENT_APPOINTMENT_SCHEDULED: { total: 0, sent: 0, failed: 0 },
          NEW_PATIENT_GENERATED: { total: 0, sent: 0, failed: 0 },
          ADDITIONAL_TREATMENT_REVENUE: { total: 0, sent: 0, failed: 0 },
        }
        for (const log of logs) {
          const ct = log.conversionType as string
          if (byType[ct]) {
            byType[ct].total++
            if (log.status === `sent`) byType[ct].sent++
            if (log.status === `failed`) byType[ct].failed++
          }
        }
        setMetrics({
          totalAttempts: total,
          successRate: total > 0 ? Math.round((sent / total) * 100) : 0,
          failureRate: total > 0 ? Math.round((failed / total) * 100) : 0,
          byType: byType as ConversionMetrics['byType'],
          recentFailures: logs.filter((l) => l.status === `failed`),
          alertsTriggered: [],
        })
        setRecentLogs({ hits: logs, total })
      })
      .catch(() => {})
  }, [pageParam])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const conversionTypeData = useMemo(() => {
    return Object.entries(metrics.byType).map(
      ([type, stats]: [string, { total: number; sent: number; failed: number }]) => ({
        type: type.replace(/_/g, ` `).toUpperCase(),
        total: stats.total,
        sent: stats.sent,
        failed: stats.failed,
        successRate: stats.total > 0 ? Math.round((stats.sent / stats.total) * 100) : 0,
      }),
    )
  }, [metrics.byType])

  const recentAttemptsData = useMemo(() => {
    return {
      hits: ((recentLogs?.hits as unknown[]) || []).map((log: unknown) => {
        const conversionLog = log as Record<string, any>
        return {
          id: conversionLog.id,
          contactId: conversionLog.contactId,
          locationId: conversionLog.locationId,
          workspaceId: conversionLog.workspaceId,
          orgId: conversionLog.orgId,
          conversionType: conversionLog.conversionType,
          status: conversionLog.status,
          gclid: conversionLog.gclid,
          conversionDateTime: conversionLog.conversionDateTime,
          conversionValue: conversionLog.conversionValue,
          errorMessage: conversionLog.errorMessage,
          googleAdsAccountId: conversionLog.googleAdsAccountId,
          apiResponse: conversionLog.apiResponse,
          createdDate: conversionLog.createdDate,
          updatedDate: conversionLog.updatedDate,
        }
      }),
      total: recentLogs?.total || 0,
    }
  }, [recentLogs])

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set(`tab`, tab)
    newSearchParams.delete(`page`)
    navigate(`?${newSearchParams.toString()}`, { replace: true })
  }

  const contactHandler = (contactId: string) => {
    if (focusedContact?.id === contactId) return
    setFocusedContact(undefined)
    contactStore
      .fetchOne(contactId)
      .then((result) => {
        const data = (result.data as any)?.contact as ContactJson | undefined
        if (data?.id) {
          setFocusedContact(data)
        } else {
          closeDrawerHandler()
        }
      })
      .catch(() => closeDrawerHandler())
    const url = new URL(window.location.href)
    url.searchParams.set(`drawer`, contactId)
    window.history.replaceState({}, ``, url.toString())
    setOpenContactDrawer(true)
  }

  const closeDrawerHandler = () => {
    setFocusedContact(undefined)
    setOpenContactDrawer(false)
    const url = new URL(window.location.href)
    url.searchParams.delete(`drawer`)
    window.history.replaceState({}, ``, url.toString())
  }

  useEffect(() => {
    const drawerId = searchParams.get(`drawer`)
    if (drawerId && drawerId !== `new` && !focusedContact) {
      contactHandler(drawerId)
    }
  }, [])

  const recentAttemptsColumns = useMemo(
    () => [
      {
        id: `date`,
        header: `Date`,
        accessorKey: `createdDate`,
        cell: ({ row }: { row: { original: { createdDate: string } } }) => (
          <div className="text-sm">
            {formatDate({ value: new Date(row.original.createdDate), format: `MMM DD, HH:mm` })}
          </div>
        ),
      },
      {
        id: `contactId`,
        header: `Contact ID`,
        accessorKey: `contactId`,
        cell: ({ row }: { row: { original: { contactId: string } } }) => (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              contactHandler(row.original.contactId)
            }}
            className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            {row.original.contactId.slice(0, 8)}...
          </a>
        ),
      },
      {
        id: `type`,
        header: `Type`,
        accessorKey: `conversionType`,
        cell: ({ row }: { row: { original: { conversionType: string } } }) => (
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium capitalize text-blue-800">
            {row.original.conversionType.replace(/_/g, ` `)}
          </span>
        ),
      },
      {
        id: `status`,
        header: `Status`,
        accessorKey: `status`,
        cell: ({ row }: { row: { original: { status: string } } }) => (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              row.original.status === `sent`
                ? `bg-green-100 text-green-800`
                : row.original.status === `failed`
                  ? `bg-red-100 text-red-800`
                  : `bg-yellow-100 text-yellow-800`
            }`}
          >
            {row.original.status}
          </span>
        ),
      },
      {
        id: `value`,
        header: `Value`,
        accessorKey: `conversionValue`,
        cell: ({ row }: { row: { original: { conversionValue: number | null } } }) => (
          <div className="text-sm">
            {row.original.conversionValue ? `$${row.original.conversionValue.toFixed(2)}` : `-`}
          </div>
        ),
      },
      {
        id: `error`,
        header: `Error`,
        accessorKey: `errorMessage`,
        cell: ({ row }: { row: { original: { errorMessage: string | null } } }) => (
          <div className="max-w-xs truncate text-sm" title={row.original.errorMessage || ``}>
            {row.original.errorMessage || `-`}
          </div>
        ),
      },
      {
        id: `account`,
        header: `Account`,
        accessorKey: `googleAdsAccountId`,
        cell: ({ row }: { row: { original: { googleAdsAccountId: string } } }) => (
          <div className="font-mono text-sm">
            {row.original.googleAdsAccountId !== `unknown` ? row.original.googleAdsAccountId : `-`}
          </div>
        ),
      },
    ],
    [],
  )

  return {
    metrics,
    activeTab,
    handleTabChange,
    focusedContact,
    openContactDrawer,
    contactHandler,
    closeDrawerHandler,
    conversionTypeData,
    recentAttemptsData,
    recentAttemptsColumns,
    setFocusedContact,
  }
}
